import type { ComponentType } from "react";
import type { IconWeight } from "@phosphor-icons/react/dist/lib/types";

type IconSize = "2xs" | "xs" | "sm" | "md" | "lg" | "xl" | "2xl" | "3xl";

// Up to `xl` these are icons sitting beside text. `2xl` and `3xl` are for an
// icon that *is* the content — the picture on a choice card, an empty state's
// glyph — where a 24px mark next to a heading looks like an afterthought.
const sizeClasses: Record<IconSize, string> = {
  "2xs": "h-2.5 w-2.5",
  xs: "h-3 w-3",
  sm: "h-3.5 w-3.5",
  md: "h-4 w-4",
  lg: "h-5 w-5",
  xl: "h-6 w-6",
  "2xl": "h-8 w-8",
  "3xl": "h-12 w-12",
};

interface IconProps {
  icon: ComponentType<{ className?: string; weight?: IconWeight }>;
  size?: IconSize;
  weight?: IconWeight;
  color?: string;
  spinning?: boolean;
  /**
   * Flow the icon inline within surrounding text (leading glyph on a line of
   * copy) instead of as a flex/block element. Needed when the icon sits inside
   * a `<T>` that a parent truncates with `text-overflow: ellipsis` — a block or
   * flex icon breaks that. Aligns the glyph to the text via a small offset.
   */
  inline?: boolean;
}

export function Icon({
  icon: PhosphorIcon,
  size = "md",
  weight = "duotone",
  color,
  spinning,
  inline,
}: IconProps) {
  return (
    <PhosphorIcon
      className={`${sizeClasses[size]} ${spinning ? "animate-spin" : ""} ${color ?? ""} ${inline ? "inline-block align-[-0.15em]" : "shrink-0"}`}
      weight={weight}
    />
  );
}

export type { IconProps };
